import classNames from 'classnames/bind';
import { useMemo, useState } from 'react';
import Loading from 'common/loading';
import { Modal } from 'contexts/modalFunc';
import { OrderType } from 'everywhere/common';
import EmptyState from 'everywhere/common/list/empty';
import ErrorState from 'everywhere/common/list/error';
import styles from 'everywhere/common/list/index.module.scss';
import RecommendItem from 'everywhere/common/list/recommendItem';
import SortOption from 'everywhere/common/list/sortOption';
import { IRecommend } from 'everywhere/monthly/types';

const cx = classNames.bind(styles);

interface IProps {
  data: IRecommend[];
  error: boolean;
  loading: boolean;
  onReset(): void;
  onSelect(type: Modal, item: IRecommend): void;
}

const compareFunctions = new Map([
  [
    OrderType.RANK,
    (a: IRecommend, b: IRecommend) =>
      a.arrivalCity.popularity - b.arrivalCity.popularity,
  ],
  [
    OrderType.PRICE,
    (a: IRecommend, b: IRecommend) =>
      a.fare - b.fare || a.arrivalCity.popularity - b.arrivalCity.popularity,
  ],
  [
    OrderType.DURATION,
    (a: IRecommend, b: IRecommend) =>
      a.duration - b.duration ||
      a.arrivalCity.popularity - b.arrivalCity.popularity,
  ],
]);

export default function List({
  error,
  data,
  loading,
  onReset,
  onSelect,
}: IProps) {
  const [sortOrder, setSortOrder] = useState(OrderType.PRICE);
  const sorted = useMemo(
    () => [...data].sort(compareFunctions.get(sortOrder)),
    [data, sortOrder],
  );
  const isCompleted = !loading && !error;

  return (
    <div className={cx('List')}>
      <div className={cx('inner')}>
        <div className={cx('list_header')}>
          <h3 className={cx('title')}>
            <b className={cx('name')}>추천</b>
            <i className={cx('count')}>{data.length}</i>
          </h3>
          <div className={cx('sort')}>
            {[
              [OrderType.RANK, '인기순', 'rkd'],
              [OrderType.PRICE, '가격순', 'prd'],
              [OrderType.DURATION, '소요시간순', 'dud'],
            ].map(([value, label, clickCode]) => (
              <SortOption
                key={value}
                clickCode={clickCode}
                isChecked={sortOrder === value}
                label={label}
                onClick={() => {
                  setSortOrder(value as OrderType);
                }}
              />
            ))}
          </div>
        </div>
        <div className={cx('list')}>
          {loading && <Loading />}
          {error && <ErrorState />}
          {isCompleted && data.length === 0 && <EmptyState onReset={onReset} />}
          {isCompleted &&
            sorted.map((item: IRecommend) => (
              <RecommendItem
                key={item.arrivalCity.iataCode}
                cityImage={item.cityImage}
                departureCityName={item.departureCity.cityName}
                departureCityCode={item.departureCity.iataCode}
                arrivalCityName={item.arrivalCity.cityName}
                arrivalCityCode={item.arrivalCity.iataCode}
                stops={item.stops}
                duration={item.duration}
                price={item.fare}
                onClick={() => {
                  onSelect(Modal.RECOMMEND, item);
                }}
                onOpenChart={() => {
                  onSelect(Modal.FARE_CHART, item);
                }}
              />
            ))}
        </div>
      </div>
    </div>
  );
}
